/**
 * WCAG 2.1 contrast helpers. Used for the company --primary override (8.12.2: rejected if it fails WCAG AA) and
 * for automatic text colour on heat tiles (8.12.4 "matn kontrasti avtomatik").
 */

export const WCAG_AA_NORMAL_TEXT_RATIO = 4.5; // WCAG 2.1 SC 1.4.3
export const WCAG_AA_LARGE_TEXT_RATIO = 3; // WCAG 2.1 SC 1.4.3 (large text) and 1.4.11 (non-text)

export interface RgbColor {
  red: number;
  green: number;
  blue: number;
}

export class InvalidColorError extends Error {
  constructor(colorText: string) {
    super(`Unsupported colour value: ${colorText}`);
    this.name = 'InvalidColorError';
  }
}

const HEX_COLOR_PATTERN = /^#([0-9a-f]{3}|[0-9a-f]{6})$/i;

export function isHexColor(colorText: string): boolean {
  return HEX_COLOR_PATTERN.test(colorText.trim());
}

export function parseHexColor(colorText: string): RgbColor {
  const trimmedColor = colorText.trim();
  if (!HEX_COLOR_PATTERN.test(trimmedColor)) {
    throw new InvalidColorError(colorText);
  }
  const hexDigits = trimmedColor.slice(1);
  const fullHexDigits =
    hexDigits.length === 3
      ? hexDigits
          .split('')
          .map((hexDigit) => hexDigit + hexDigit)
          .join('')
      : hexDigits;
  return {
    red: Number.parseInt(fullHexDigits.slice(0, 2), 16),
    green: Number.parseInt(fullHexDigits.slice(2, 4), 16),
    blue: Number.parseInt(fullHexDigits.slice(4, 6), 16),
  };
}

function linearizeChannel(channelValue: number): number {
  const normalizedChannel = channelValue / 255;
  return normalizedChannel <= 0.03928
    ? normalizedChannel / 12.92
    : Math.pow((normalizedChannel + 0.055) / 1.055, 2.4);
}

export function relativeLuminance(color: RgbColor): number {
  return (
    0.2126 * linearizeChannel(color.red) +
    0.7152 * linearizeChannel(color.green) +
    0.0722 * linearizeChannel(color.blue)
  );
}

/** Contrast ratio between two hex colours, 1..21. */
export function contrastRatio(firstHexColor: string, secondHexColor: string): number {
  const firstLuminance = relativeLuminance(parseHexColor(firstHexColor));
  const secondLuminance = relativeLuminance(parseHexColor(secondHexColor));
  const lighterLuminance = Math.max(firstLuminance, secondLuminance);
  const darkerLuminance = Math.min(firstLuminance, secondLuminance);
  return (lighterLuminance + 0.05) / (darkerLuminance + 0.05);
}

export const LIGHT_TEXT_ON_DARK = '#ffffff';
export const DARK_TEXT_ON_LIGHT = '#0d1220';

/** Picks white or ink text for a background so the pair has the higher contrast (heat tiles, badges). */
export function readableTextColor(backgroundHexColor: string): string {
  const contrastWithLight = contrastRatio(backgroundHexColor, LIGHT_TEXT_ON_DARK);
  const contrastWithDark = contrastRatio(backgroundHexColor, DARK_TEXT_ON_LIGHT);
  return contrastWithLight >= contrastWithDark ? LIGHT_TEXT_ON_DARK : DARK_TEXT_ON_LIGHT;
}

export interface PrimaryColorContrastReport {
  primaryColor: string;
  /** Button label colour chosen for the primary background. */
  buttonTextColor: string;
  buttonTextRatio: number;
  /** Primary used as text/line on the card background. */
  onCardRatio: number;
  passesWcagAa: boolean;
}

/**
 * 8.12.2: a company primary colour replaces --primary only when it passes WCAG AA both as a button background
 * (with its label) and as link/text colour on the card surface of the given scheme.
 */
export function checkPrimaryColorContrast(
  primaryColor: string,
  cardBackgroundColor: string,
): PrimaryColorContrastReport {
  const buttonTextColor = readableTextColor(primaryColor);
  const buttonTextRatio = contrastRatio(primaryColor, buttonTextColor);
  const onCardRatio = contrastRatio(primaryColor, cardBackgroundColor);
  return {
    primaryColor,
    buttonTextColor,
    buttonTextRatio,
    onCardRatio,
    passesWcagAa: buttonTextRatio >= WCAG_AA_NORMAL_TEXT_RATIO && onCardRatio >= WCAG_AA_LARGE_TEXT_RATIO,
  };
}
